import {Component, OnInit} from '@angular/core';
import {GeneralService} from "../../../../services/generalService";
import {RouteService} from "../../../../services/routeService";
import {NotificationService} from "../../../../services/notificationService";
import {DICTIONARY} from "../../../../dictionary";
import _ from 'lodash';
import {CollaborationProtectionService} from "../../../../services/collaborationProtectionService";

@Component({
	selector: 'app-collaboration-mapping',
	templateUrl: './collaboration-mapping.component.html'
})
export class CollaborationMappingComponent implements OnInit {
	constructor(public gs:GeneralService,
				private rs:RouteService,
				private cps:CollaborationProtectionService,
				private ns:NotificationService) {
	}

	_ = _;
	dic = DICTIONARY;
	mailboxes;
	resources;
	usersPopup;
	activeSpecificUsers;
	usersFilterTypes = {all: 'all', specific: 'specific'};

	loadingInProcess;

	dashboardPeriod = [
		this.dic.CONSTANTS.trendsPeriod.last3Days,
		this.dic.CONSTANTS.trendsPeriod.lastWeek,
		this.dic.CONSTANTS.trendsPeriod.lastMonth,
		this.dic.CONSTANTS.trendsPeriod.all
	];
	period = this.dic.CONSTANTS.trendsPeriod.lastWeek;

	resourceGraph;
	// CC: node config supplied to echarts-graph (User/Channel/Chat/Meeting/Drive)
	graphNodeCfg = {
		green: {
			color: '#2ecc71', label: 'User', symbol: 'circle', icon: '\uf007',
			sizeMultiplier: 1,
		},
		blue: {
			color: '#4d8af0', label: 'Channel', symbol: 'circle', icon: '\uf292',
			sizeMultiplier: 1.3,
		},
		red: {
			color: '#e05252', label: 'Chat', symbol: 'circle', icon: '\uf075',
			sizeMultiplier: 1.3,
		},
		purple: {
			color: '#9b59b6', label: 'Meeting', symbol: 'circle', icon: '\uf03d',
			sizeMultiplier: 1.3,
		},
		orange: {
			color: '#e8a838', label: 'Drive', symbol: 'circle', icon: '\uf0c2',
			sizeMultiplier: 1.3,
		},
	};
	viewChannel = true;
	viewDrive = false;
	viewChats = false;
	viewMeeting = false;

	ngOnInit() {
		// this.makeDummies();
		// this.renderResourcesGraph();
		this.getCollaborationUsersMailboxes();
	}


	getCollaborationUsersMailboxes = () => {
		this.loadingInProcess = true;

		return Promise.all([
			this.rs.getCollaborationResources(),
			this.rs.getCollaborationUsersMailboxes(),
		]).then(([resources, mailboxes]) => {
			this.resources = resources;
			this.resources.forEach((resourceObj) => {
				this.cps.parseResourceName(resourceObj);
			});
			this.mailboxes = mailboxes;

			this.renderResourcesGraph();
		}).catch((err) => {
			this.loadingInProcess = false;

			console.error(err);
		}).finally(() => {
			this.loadingInProcess = false;
		});
	};

	openUsersFilterPopup = () => {
		// Shallow-spread each mailbox so the table can mutate `selected`/`hide`
		// without touching the source array. Much faster than _.cloneDeep.
		let users;
		if (this.activeSpecificUsers) {
			const selectedEmails = new Set(this.activeSpecificUsers.map((u: any) => u.email));
			users = this.mailboxes.map((m: any) => ({ ...m, selected: selectedEmails.has(m.email) }));
		}
		else {
			users = this.mailboxes.map((m: any) => ({ ...m }));
		}

		this.usersPopup = {
			type: this.activeSpecificUsers ? this.usersFilterTypes.specific : this.usersFilterTypes.all,
			users,
			show: true,

			searchUsers: (searchTerm) => {
				searchTerm = searchTerm.toLowerCase();
				this.usersPopup.users.forEach(record => {
					if (record.email?.toLowerCase().includes(searchTerm) || record.name?.toLowerCase().includes(searchTerm)) {
						record.hide = false;
					}
					else {
						record.hide = true;
					}
				});
			},

			apply: () => {
				if (this.usersPopup.type === this.usersFilterTypes.specific && !_.find(this.usersPopup.users, 'selected')) {
					this.ns.showErrorMessage('You must select at least one user');
					return;
				}

				if (this.usersPopup.type === this.usersFilterTypes.specific) {
					this.activeSpecificUsers = _.filter(this.usersPopup.users, 'selected');
				}
				else {
					this.activeSpecificUsers = null;
				}

				this.usersPopup = null;
				this.renderResourcesGraph();
			}
		};
	}

	toggleResourceType = (type: 'channel' | 'drive' | 'chats' | 'meeting') => {
		if (this.loadingInProcess) return;

		switch (type) {
			case 'channel':
				this.viewChannel = !this.viewChannel;
				break;

			case 'drive':
				this.viewDrive = !this.viewDrive;
				break;

			case 'chats':
				this.viewChats = !this.viewChats;
				break;

			case 'meeting':
				this.viewMeeting = !this.viewMeeting;
				break;
		}

		this.renderResourcesGraph();
	}

	changePeriod = (periodObj) => {
		if (this.period.value === periodObj.value) {
			return;
		}

		this.period = periodObj;
		this.renderResourcesGraph();
	}

	renderResourcesGraph = () => {
		const users = this.activeSpecificUsers || this.mailboxes;

		// Pre-build O(1) lookup structures — avoids repeated linear scans inside nodeOptionsFn
		const mailboxEmailSet = new Set<string>(this.mailboxes.map((m: any) => m.email));
		const resourceById = new Map<string, any>();
		const resourceByName = new Map<string, any>();
		for (const r of this.resources) {
			if (r.id != null) resourceById.set(String(r.id), r);
			if (r._id != null && String(r._id) !== String(r.id)) resourceById.set(String(r._id), r);
			if (r.name) resourceByName.set(r.name, r);
		}

		// Pre-build member → resources index — avoids O(users × resources × members) filter per user
		// Meetings may expose participants via attendees/participants instead of members
		const memberResourcesMap = new Map<string, any[]>();
		for (const resource of this.resources) {
			const memberList: string[] = resource.members || resource.attendees || resource.participants || [];
			for (const email of memberList) {
				if (!memberResourcesMap.has(email)) memberResourcesMap.set(email, []);
				memberResourcesMap.get(email).push(resource);
			}
		}

		// Compute cutoff date once for the selected period (null = no cutoff / "all")
		const cutoff = this.getPeriodCutoff(this.period.value);

		// Lazy-built link-target count map, populated on the first nodeOptionsFn call
		let linkTargetCount: Map<string, number> | null = null;

		this.resourceGraph = {
			graphData: [],
			nodeOptionsFn: (node, allLinks) => {
				if (!linkTargetCount) {
					linkTargetCount = new Map();
					for (const l of allLinks) {
						linkTargetCount.set(l.target, (linkTargetCount.get(l.target) || 0) + 1);
					}
				}
				const sentToCounter = linkTargetCount.get(node.name) || 0;
				const isUserNode = mailboxEmailSet.has(node.name);

				if (isUserNode) {
					node.color = 'green';
				}
				else {
					const resourceObj = resourceById.get(node.name) || resourceByName.get(node.name);
					if (!resourceObj) return;
					node.name = resourceObj.name || node.name;

					switch (resourceObj.resource_type) {
						case 'chat': {
							node.color = 'red';
							const chatName = resourceObj.topic || resourceObj.subject || resourceObj.name;
							if (!chatName) {
								const members = (allLinks as any[])
									.filter((l: any) => String(l.target) === String(node.id))
									.map((l: any) => (l.source || '').split('@')[0])
									.slice(0, 2);
								node.name = members.length ? members.join(' & ') : 'Chat';
							}
							const chatMembers = resourceObj.members || resourceObj.attendees || resourceObj.participants;
							if (chatMembers?.length) {
								node.tooltipExtra = this.formatMembersTooltip(chatMembers);
							}
							break;
						}

						case 'channel':
							node.color = 'blue';
							if (resourceObj.members?.length) {
								node.tooltipExtra = this.formatMembersTooltip(resourceObj.members);
							}
							break;

						case 'drive':
							node.color = 'orange';
							break;

						case 'meeting':
							node.color = 'purple';
							node.name = node.name || 'Meeting';
							break;
					}
				}

				node.lineWidth = sentToCounter && !isUserNode ? Math.log(Math.min(sentToCounter, 20)) * 2 : 1;

				// hide the tooltip that shows numbers of links of a node (because we show another number in label)
				node.hideTooltip = true;
			},
			nodeSearchTxt: '',
			nodeSearchResults: null
		};

		const links = [];
		for (const userObj of users) {
			const userResources = memberResourcesMap.get(userObj.email);
			if (!userResources?.length) continue;

			for (const resourceObj of userResources) {
				if (cutoff) {
					const dateStr = resourceObj.last_updated || resourceObj.start_time || resourceObj.date;
					const resourceDate = new Date(dateStr);
					if (!isFinite(resourceDate.getTime()) || resourceDate < cutoff) continue;
				}

				const resourceId = String(resourceObj.id ?? resourceObj._id ?? '');
				if (!resourceId) continue;
				switch (resourceObj.resource_type) {
					case 'chat':
						if (this.viewChats) links.push({source: userObj.email, target: resourceId});
						break;

					case 'drive':
						if (this.viewDrive) links.push({source: userObj.email, target: resourceId});
						break;

					case 'channel':
						if (this.viewChannel) links.push({source: userObj.email, target: resourceId});
						break;

					case 'meeting':
						if (this.viewMeeting) links.push({source: userObj.email, target: resourceId});
						break;
				}
			}
		}

		// cap to 5000 links — keep the most-connected resources
		const MAX_LINKS = 1000;
		let cappedLinks = links;
		if (links.length > MAX_LINKS) {
			const targetCount = new Map<string, number>();
			for (const l of links) {
				targetCount.set(l.target, (targetCount.get(l.target) || 0) + 1);
			}
			cappedLinks = links.sort((a, b) => (targetCount.get(b.target) || 0) - (targetCount.get(a.target) || 0)).slice(0, MAX_LINKS);
		}

		setTimeout(() => {
			this.resourceGraph.graphData = {links: cappedLinks};
		});
	}

	makeDummies = () => {
		const resourceTypes = ['channel', 'drive', 'chat', 'meeting'];
		const dummyMailboxes = [];
		const dummyResources = [];

		// generate 1,000 users
		for (let i = 0; i < 1000; i++) {
			dummyMailboxes.push({
				email: `user${i}@dummy.com`
			});
		}

		// generate 100,000 resources, each with 1-5 random members
		for (let i = 0; i < 100000; i++) {
			const type = resourceTypes[i % 4];
			const memberCount = (i % 5) + 1;
			const members = [];
			for (let j = 0; j < memberCount; j++) {
				members.push(`user${(i + j) % 1000}@dummy.com`);
			}

			const daysAgo = i % 30;
			const date = new Date();
			date.setDate(date.getDate() - daysAgo);

			dummyResources.push({
				id: `res_${i}`,
				_id: `res_${i}`,
				name: `${type}_resource_${i}`,
				resource_type: type,
				members,
				last_updated: date.toISOString()
			});
		}

		this.mailboxes = dummyMailboxes;
		this.resources = dummyResources;
		this.renderResourcesGraph();
	}

	// formats members list for tooltip — 2 per row, max 10, with ellipsis
	private formatMembersTooltip(members: string[]): string {
		const truncated = members.length > 10;
		const visible = truncated ? members.slice(0, 10) : members;
		const rows = [];
		for (let i = 0; i < visible.length; i += 2) {
			const pair = visible.slice(i, i + 2).join(', ');
			rows.push(pair);
		}
		if (truncated) {
			rows.push('...');
		}
		return 'Members:<br/>' + rows.join('<br/>');
	}

	private getPeriodCutoff = (period): Date | null => {
		const now = new Date();
		switch (period) {
			case this.dic.CONSTANTS.trendsPeriod.last3Days.value: {
				const d = new Date(now); d.setDate(now.getDate() - 3); return d;
			}
			case this.dic.CONSTANTS.trendsPeriod.lastWeek.value: {
				const d = new Date(now); d.setDate(now.getDate() - 7); return d;
			}
			case this.dic.CONSTANTS.trendsPeriod.lastMonth.value: {
				const d = new Date(now); d.setMonth(now.getMonth() - 1); return d;
			}
			default:
				return null; // 'all' — no cutoff
		}
	}
}

